import { type MutableRefObject, useCallback, useContext, useEffect, useRef } from 'react';

import { AnalyticsContext } from '@src/features/analytics/contexts';
import { TripDetailsPage } from '@src/generated/dato-cms';
import { segmentService } from '@src/services';
import { ArgsOf } from '@src/utils/types';

import { TrackingEvent } from '../types';

let bufferedRequests: (() => void)[] = [];

const useBufferedTracking = <T>(
  trackMethod: (args: T) => Promise<void>,
  bufferedRequests: (() => void)[],
  segmentLoadedRef: MutableRefObject<boolean>,
) => {
  return useCallback(
    async (...args: ArgsOf<typeof trackMethod>) => {
      if (segmentLoadedRef.current) {
        await trackMethod(...args);
        return;
      }

      bufferedRequests.push(() => {
        return trackMethod(...args);
      });
    },
    [trackMethod, bufferedRequests, segmentLoadedRef],
  );
};

export const useAnalytics = () => {
  const { segmentLoaded, profile, trip, property, unitGroup, updateProfile, updateTrip } = useContext(AnalyticsContext);
  const segmentLoadedRef = useRef(segmentLoaded);

  useEffect(() => {
    if (segmentLoaded) {
      bufferedRequests.forEach((fn) => fn());
      bufferedRequests = [];
    }
  }, [segmentLoaded]);

  useEffect(() => {
    segmentLoadedRef.current = segmentLoaded;
  }, [segmentLoaded]);

  const trackEvent = useBufferedTracking(
    segmentService.trackEvent.bind(segmentService),
    bufferedRequests,
    segmentLoadedRef,
  );
  const trackPage = useBufferedTracking(
    segmentService.trackPage.bind(segmentService),
    bufferedRequests,
    segmentLoadedRef,
  );
  const trackIdentification = useBufferedTracking(
    segmentService.trackIdentification.bind(segmentService),
    bufferedRequests,
    segmentLoadedRef,
  );

  const getKitchenType = (amenities?: NonNullable<TripDetailsPage['unitGroup']>['amenities']) => {
    return amenities?.find(({ amenityId }) => /kitchen/i.test(amenityId))?.amenityId ?? 'none';
  };

  // property is only available on the trip details page
  const currentTripData =
    property && unitGroup && trip
      ? {
          reservation_id: trip.id,
          property_id: trip.property.id,
          unit_group_id: trip.unitGroup.id,
          is_breakfast_booked: !!trip.breakfast?.isBooked,
          channel: trip.channelCode,
          city: property.city.slug,
          country: property.country.slug,
          kitchen_type: getKitchenType(unitGroup.amenities),
          travel_purpose_questionnaire_reason: trip.travelPurposeQuestionnaire?.travelPurpose,
          travel_purpose_questionnaire_activity: trip.travelPurposeQuestionnaire?.activities,
          billing_travel_purpose: trip.travelPurpose,
        }
      : {};

  const commonEventProperties = {
    profile_id: profile?.id,
    is_logged_in: !!profile?.id,
    ...currentTripData,
  };

  return {
    trackEvent: async (trackingEvent: TrackingEvent) =>
      trackEvent({
        ...trackingEvent,
        properties: {
          ...commonEventProperties,
          ...trackingEvent.properties,
        },
      }),
    trackPage: async () => trackPage(commonEventProperties),
    trackIdentification,
    updateProfile,
    updateTrip,
  };
};
